import {TreeStyle} from '@xh/hoist/cmp/grid';
import {HoistModel, lookup, managed, XH} from '@xh/hoist/core';
import {FilterLike, View} from '@xh/hoist/data';
import {DashViewModel} from '@xh/hoist/desktop/cmp/dash';
import {Icon} from '@xh/hoist/icon';
import {Timer} from '@xh/hoist/utils/async';
import {MINUTES} from '@xh/hoist/utils/datetime';
import {uniq} from 'lodash';
import {isPortfolioRow} from '../../../../cmp/PositionColumns';
import {ConfigurableGridModel} from '../../../../cmp/gridSpec/ConfigurableGridModel';
import {buildFilter, lockSingleLeafGroups} from '../../../../cmp/gridSpec/GridSpecBuilder';
import {leafColumns} from '../../../../cmp/gridSpec/GridSpecOps';
import {GridSpec} from '../../../../cmp/gridSpec/GridSpecTypes';
import {HoldingsTypeEnum} from '../../../../data/DataTypes';
import {
    migratePositionsState,
    positionsBaseColumn,
    positionsCapabilities,
    positionsCatalog,
    positionsDefaultSpec,
    positionsStoreFields
} from './PositionsGridSpec';

/**
 * Live positions, as a tree grid over a Cube View. The grid is configured by a GridSpec (manually
 * or via the AI assistant) - grouping, filters and leaf rows are applied here to the View query.
 */
export class PositionsWidgetModel extends HoistModel {
    @managed configurableGridModel: ConfigurableGridModel;
    @managed view: View;
    @managed private portfolioSeriesTimer: Timer;
    @lookup(DashViewModel) dashViewModel: DashViewModel;

    get gridModel() {
        return this.configurableGridModel.gridModel;
    }

    get spec(): GridSpec {
        return this.configurableGridModel.specModel.spec;
    }

    override onLinked(): void {
        super.onLinked();

        const {dashViewModel} = this,
            viewState = dashViewModel.viewState;

        dashViewModel.extraMenuItems.push({
            icon: Icon.gear(),
            text: 'Configure',
            actionFn: () => this.configurableGridModel.openConfigurator()
        });

        this.configurableGridModel = new ConfigurableGridModel({
            title: 'Positions',
            catalog: positionsCatalog,
            capabilities: positionsCapabilities,
            defaultSpec: positionsDefaultSpec,
            initialSpec: viewState?.gridSpec ?? migratePositionsState(viewState?.positionsState),
            persistCallback: gridSpec => dashViewModel.setViewState({gridSpec}),
            baseColumn: positionsBaseColumn,
            treeColumn: 'ticker',
            summaryRecords: store => store.records.filter(r => !r.parentId),
            gridConfig: {
                store: {fields: positionsStoreFields, projectionOnly: true},
                treeMode: true,
                treeStyle: TreeStyle.NONE,
                autosizeOptions: {mode: 'onDemand'}
            }
        });

        this.view = XH.livePositionsService.cube.createView({
            query: this.viewQuery,
            stores: this.gridModel.store,
            connect: true
        });

        this.addReaction({
            track: () => [this.spec.groupBy, this.spec.filters, this.spec.showLeafRows],
            run: () => this.view.updateQuery(this.viewQuery)
        });

        // Portfolio rows chart their balance in the Today column. The backend records those series, so load each as its
        // row appears and refresh them all once a minute, as the Portfolios tab does.
        this.addReaction({
            track: () => this.chartedPortfolioIds.join(','),
            run: () => this.chartedPortfolioIds.forEach(id => XH.intradayService.loadPortfolioAsync(id)),
            fireImmediately: true
        });
        this.portfolioSeriesTimer = Timer.create({
            runFn: () => Promise.all(this.chartedPortfolioIds.map(id => XH.intradayService.loadPortfolioAsync(id, true))),
            interval: MINUTES,
            delay: true
        });
    }

    /** Portfolios on screen whose balance the Today column draws - none unless that column is shown. */
    private get chartedPortfolioIds(): number[] {
        const showsToday = leafColumns(this.spec).some(c => c.field === 'intraday' && !c.hidden);
        if (!showsToday) return [];

        return uniq(this.gridModel.store.allRecords.filter(isPortfolioRow).map(r => r.data.portfolioId as number)).sort();
    }

    private get viewQuery() {
        const {spec} = this;
        return {
            includeLeaves: spec.showLeafRows,
            lockFn: lockSingleLeafGroups,
            dimensions: spec.groupBy,
            filter: [this.portfolioTypeFilter, ...buildFilter(spec)] as FilterLike
        };
    }

    private get portfolioTypeFilter(): FilterLike {
        return {field: 'portfolioHoldingType', op: '!=', value: HoldingsTypeEnum.Cash};
    }
}
